<script setup lang="ts">
import type { ICountry, IFormInput } from "@/types/global"
import type { PropType } from "vue"
import PdfModal from "@/app/components/modal/PdfModal.vue"
import BaseButton from "@/app/components/ui/BaseButton.vue"
import BasePhoneField from "@/app/components/ui/BasePhoneField.vue"
import BaseTextField from "@/app/components/ui/BaseTextField.vue"
import { useConfig } from "@/app/composables/useConfig"
import { useI18n } from "@/app/composables/useI18n"
import { useReferAndEarn } from "@/app/composables/useReferAndEarn.js"
import { fetchCountriesData } from "@/user-account/api"
import { computed, ref, watch } from "vue"
import { useValidation } from "../../composables/useValidation"

const props = defineProps({
  inputs: {
    type: Array as PropType<IFormInput[]>,
    required: true,
  },
})

const emit = defineEmits(["submit", "toLogin"])

const localInputs = ref<IFormInput[]>([...props.inputs])
const { validateInputs } = useValidation()

const { referAndEarn } = useReferAndEarn()
const { registrationFlow, files } = useConfig()

const { t } = useI18n()

const countries = ref<ICountry[]>([])
const termsAccepted = ref(true)
const marketingAccepted = ref(true)
const formSubmitted = ref(false)
const brandName = import.meta.env.VITE_APP_BRAND
const capitalizedBrandName = computed(() => {
  return brandName.charAt(0).toUpperCase() + brandName.slice(1)
})
const termsAndConditionFiles = computed(() => {
  return files.value.filter(file => file.type === "terms_and_conditions")
})
const pdfLink = ref("")
const showPdfModal = ref(false)

function handlePdf(e: Event, link: string) {
  e.preventDefault()
  e.stopPropagation()
  if (!link)
    return

  showPdfModal.value = true
  pdfLink.value = link
}

function handleLinkClick(e: Event) {
  const target = e.target as HTMLElement

  if (target.classList.contains("base-link")) {
    e.preventDefault()

    const action = target.getAttribute("data-action")

    if (action === "terms") {
      if (termsAndConditionFiles.value[0]?.file?.url) {
        handlePdf(e, termsAndConditionFiles.value[0].file.url)
      }
    }
    else if (action === "privacy") {
      if (termsAndConditionFiles.value[1]?.file?.url) {
        handlePdf(e, termsAndConditionFiles.value[1].file.url)
      }
    }
  }
}

function onLogin() {
  emit("toLogin")
}

const countryCodes = computed(() => {
  return countries.value?.filter(country => country.status).map(country => country.code)
})

async function fetchCountries() {
  try {
    const { data } = await fetchCountriesData()
    countries.value = data.data || []
  }
  catch (error) {
    console.error("Error fetching countries:", error)
  }
}

fetchCountries()

watch(
  () => props.inputs,
  (newInputs) => {
    localInputs.value = [...newInputs]
  },
  { deep: true },
)

function handleSubmit() {
  formSubmitted.value = true

  if (!termsAccepted.value || !marketingAccepted.value) {
    return
  }

  validateInputs(localInputs.value, t)
  const hasErrors = localInputs.value.some(input => input.error)
  if (!hasErrors) {
    emit("submit", localInputs.value)
  }
}
</script>

<template>
  <form @submit.prevent="handleSubmit">
    <div class="form-fields">
      <BasePhoneField
        v-if="
          ['number_2fa', 'number_2fa_with_email', 'email_2fa_with_number'].includes(
            registrationFlow.type,
          )
        "
        :model-value="localInputs[2].value"
        :rules="localInputs[2].rules"
        :label="localInputs[2].label"
        :placeholder="localInputs[2].placeholder"
        :type="localInputs[2].type"
        :icon-name="localInputs[2].iconName"
        :error="localInputs[2].error || ''"
        :country-codes="countryCodes"
        @update:model-value="(value) => {
          localInputs[2].value = value
          localInputs[2].error = ''
        }"
        @country-changed="localInputs[3].value = $event.iso2"
        @error="localInputs[2].error = $event"
      />

      <BaseTextField
        v-if="
          ['email_2fa', 'number_2fa_with_email', 'email_2fa_with_number'].includes(
            registrationFlow.type,
          )
        "
        :model-value="localInputs[localInputs.length - 1].value"
        :rules="localInputs[localInputs.length - 1].rules"
        :label="localInputs[localInputs.length - 1].label"
        :placeholder="localInputs[localInputs.length - 1].placeholder"
        :type="localInputs[localInputs.length - 1].type"
        :icon-name="localInputs[localInputs.length - 1].iconName"
        :error="localInputs[localInputs.length - 1].error || ''"
        @update:model-value="(value) => {
          localInputs[localInputs.length - 1].value = value
          localInputs[localInputs.length - 1].error = ''
        }"
        @error="localInputs[localInputs.length - 1].error = $event"
      />

      <BaseTextField
        :model-value="localInputs[0].value"
        :rules="localInputs[0].rules"
        :label="localInputs[0].label"
        :placeholder="localInputs[0].placeholder"
        :type="localInputs[0].type"
        :icon-name="localInputs[0].iconName"
        :error="localInputs[0].error || ''"
        @update:model-value="(value) => {
          localInputs[0].value = value
          localInputs[0].error = ''
        }"
        @error="localInputs[0].error = $event"
      />

      <BaseTextField
        v-if="referAndEarn.enabled"
        :model-value="localInputs[1].value"
        :rules="localInputs[1].rules"
        :label="localInputs[1].label"
        :placeholder="localInputs[1].placeholder"
        :type="localInputs[1].type"
        :icon-name="localInputs[1].iconName"
        :error="localInputs[1].error || ''"
        @update:model-value="(value) => {
          localInputs[1].value = value
          localInputs[1].error = ''
        }"
        @error="localInputs[1].error = $event"
      />
    </div>

    <div class="terms">
      <label class="checkbox-container">
        <input
          v-model="marketingAccepted"
          type="checkbox"
          required
        >
        <span>{{ t('auth.marketing_consent', { brand: capitalizedBrandName }) }}</span>
        <span class="checkmark" />
      </label>
      <small v-if="!marketingAccepted && formSubmitted" class="error">
        {{ t("auth.marketing_consent_required") }}
      </small>
      <label class="checkbox-container">
        <input
          v-model="termsAccepted"
          type="checkbox"
          required
        >
        <span
          @click="handleLinkClick"
          v-html="t('auth.terms_consent', {
            terms: `<a class='base-link' data-action='terms'>${t('general.t_and_c')}</a>`,
            privacy: `<a class='base-link' data-action='privacy'>${t('general.privacy_policy')}</a>`,
          })"
        />
        <span class="checkmark" />
      </label>
      <small v-if="!termsAccepted && formSubmitted" class="error">
        {{ t("auth.terms_consent_required") }}
      </small>
    </div>

    <BaseButton
      :disabled="localInputs.some(input => input.error) || !termsAccepted || !marketingAccepted"
      class="submit alight wide high"
      type="submit"
    >
      {{ registrationFlow?.allow_personal_info ? t("auth.next") : t("auth.register") }}
    </BaseButton>

    <div class="dont-have-account">
      <p>
        {{ t("account.already_account_label") }}
      </p>
    </div>

    <button class="login-now" type="button" @click="onLogin">
      {{ t("account.login_now_label") }}
    </button>
  </form>

  <PdfModal
    :show="showPdfModal"
    :link="pdfLink"
    @close="() => (showPdfModal = false)"
  />
</template>

<style lang="scss" scoped>
.form-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}

.terms {
  font-size: 14px;
  margin-bottom: 1rem;
}

.login-now {
  text-align: center;
  color: var(--be-primary-btn);
  font-size: 14px;
  width: 100%;
}
.dont-have-account {
  font-size: 14px;
  margin-bottom: 10px;
  text-align: center;
}
.terms {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
</style>
